import type { WhatsappIntegration, WhatsappProviderSummary } from '@chukua/common';
import { Alert, Button, Form, Input, Popconfirm } from 'antd';
import { ConfigFields } from '../../../components/ConfigFields';
import { IntegrationLogo } from '../../../components/IntegrationLogo';
import { providerFormInitialValues } from '../../../policies/messaging-provider.policy';
import type { useWhatsappIntegrationSheet, WhatsappFormValues } from '../hooks/use-whatsapp-integration-sheet';

const SECTION_HELP = {
  'Sign-in codes': 'Codes go by WhatsApp once you name an approved authentication template. Until then they go by SMS.',
};

function submitLabel(isSettings: boolean, sendingName: string | undefined): string {
  if (isSettings) return 'Save';
  return sendingName ? 'Check, install and switch' : 'Check and install';
}

interface WhatsappIntegrationFormProps {
  sheet: ReturnType<typeof useWhatsappIntegrationSheet>;
  provider: WhatsappProviderSummary;
  /** The provider sending now, if any: installing another replaces it, and the form says so. */
  sendingName: string | undefined;
  /** Settings only: turning the provider off, or uninstalling one that is off. */
  onStopSending: (integration: WhatsappIntegration) => Promise<void>;
  onUninstall: (integration: WhatsappIntegration) => Promise<void>;
}

/** The provider's fields, rendered from its config schema. Saving new details checks them with the provider first, sending nothing. */
export function WhatsappIntegrationForm({ sheet, provider, sendingName, onStopSending, onUninstall }: WhatsappIntegrationFormProps) {
  const { integration } = sheet;

  return (
    <Form<WhatsappFormValues>
      layout="vertical"
      requiredMark={false}
      disabled={sheet.submitting}
      onFinish={sheet.save}
      initialValues={providerFormInitialValues(provider, integration)}
    >
      <div className="mb-5 flex items-center gap-3">
        <IntegrationLogo logoSvg={provider.logoSvg} name={provider.displayName} />
        <span className="text-base font-semibold text-fg">{provider.displayName}</span>
      </div>
      {sheet.error && <Alert className="mb-5" type="error" showIcon title={sheet.error} />}
      <p className="mb-5 text-sm text-muted">
        Messages use templates approved in your {provider.displayName} account. Saving checks these details with {provider.displayName} and sends nothing.
      </p>
      <Form.Item name="displayName" label="Name" rules={[{ required: true, message: 'Enter a name' }]}>
        <Input />
      </Form.Item>
      <ConfigFields fields={provider.fields} secretsSet={integration?.secretsSet ?? []} sectionHelp={SECTION_HELP} />
      {!integration && sendingName && (
        <Alert
          className="mb-5"
          type="info"
          showIcon
          title={`${provider.displayName} will replace ${sendingName}`}
          description={`WhatsApp messages go out through ${provider.displayName} once it is installed. ${sendingName} stays installed but off, so you can switch back.`}
        />
      )}
      <Form.Item className="mb-0 pt-2">
        <div className="flex flex-wrap gap-2">
          <Button type="primary" htmlType="submit" loading={sheet.submitting}>
            {submitLabel(Boolean(integration), sendingName)}
          </Button>
          {integration && (
            <Button loading={sheet.checking} onClick={sheet.check}>
              Check connection
            </Button>
          )}
        </div>
      </Form.Item>
      {integration && <DangerZone integration={integration} onStopSending={onStopSending} onUninstall={onUninstall} />}
    </Form>
  );
}

function DangerZone({
  integration,
  onStopSending,
  onUninstall,
}: { integration: WhatsappIntegration } & Pick<WhatsappIntegrationFormProps, 'onStopSending' | 'onUninstall'>) {
  return (
    <div className="mt-8 flex flex-col gap-3 border-t border-line pt-5">
      <h3 className="text-sm font-semibold text-fg">{integration.isSending ? 'Turn off' : 'Uninstall'}</h3>
      <p className="text-sm text-muted">
        {integration.isSending
          ? 'Notifications go by SMS until another WhatsApp provider sends.'
          : 'Removes these credentials. Messages already sent are unaffected.'}
      </p>
      <div>
        {integration.isSending ? (
          <Popconfirm title={`Turn off ${integration.displayName}?`} okText="Turn off" onConfirm={() => onStopSending(integration)}>
            <Button danger>Turn off</Button>
          </Popconfirm>
        ) : (
          <Popconfirm
            title={`Uninstall ${integration.displayName}?`}
            okText="Uninstall"
            okButtonProps={{ danger: true }}
            onConfirm={() => onUninstall(integration)}
          >
            <Button danger>Uninstall</Button>
          </Popconfirm>
        )}
      </div>
    </div>
  );
}
